// Contexto del vendedor (tienda + id) para el wizard — derivado de la SESIÓN, no de constantes.
//
// Cierra TD-F2-WIZ-003. El wizard NO tiene login propio: comparte la sesión del portal (misma SPA,
// misma cookie HttpOnly, mismo `context.auth`). De ahí sale todo:
//   - sellerId = `cognitoSub` del usuario autenticado.
//   - storeId  = `scopeRef` de la asignación de rol a nivel tienda. El permiso `retail.wizard.operate`
//     se otorga con scope LEVEL_2 (rol `vendedor`), y el `scopeRef` de esa asignación ES el storeId.
//     Ver DEC-RBAC-091: la jerarquía es PLATFORM > TENANT > LEVEL_1 (retailer) > LEVEL_2 > LEVEL_3.
//
// Un admin de tenant (scope TENANT) NO tiene tienda: cubre todo el tenant pero no "opera desde" una.
// Por eso `storeId` puede venir vacío → `hasStore=false` y el wizard lo trata como bloqueo, en vez
// de inventar una tienda y escribir la venta en el lugar equivocado.
import { useAuthStore } from '@/stores/authStore';

/** Scopes que identifican una tienda concreta, del más específico al menos. */
const STORE_SCOPES = ['LEVEL_3', 'LEVEL_2'] as const;

export interface SellerContext {
    sellerId: string;
    /** Tienda desde la que vende. Cadena vacía si la sesión no ata al usuario a una tienda. */
    storeId: string;
    /** `false` ⇒ el usuario está autenticado pero no tiene tienda asignada: no puede vender. */
    hasStore: boolean;
    /** Nombre para mostrar (topbar del wizard). */
    sellerName: string;
}

export function useSellerContext(): SellerContext {
    const user = useAuthStore((s) => s.user);
    const roleAssignments = useAuthStore((s) => s.roleAssignments);

    // El más específico gana: si alguien tuviera LEVEL_3 y LEVEL_2, la tienda es la de LEVEL_3.
    const storeAssignment = STORE_SCOPES.map((scope) =>
        roleAssignments.find((a) => a.scope === scope && a.scopeRef && a.scopeRef !== '*'),
    ).find(Boolean);

    return {
        sellerId: user?.cognitoSub ?? '',
        storeId: storeAssignment?.scopeRef ?? '',
        hasStore: Boolean(storeAssignment),
        sellerName: user?.displayName ?? '',
    };
}
